/**
 * QuickNotePanel — 420px slide-up panel for posting notes to Dynamics CRM.
 *
 * Can be used standalone or with the QuickNoteFab.
 * When defaultAccountId is provided, skips the group search.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  makeStyles,
  Select,
  Button,
  Textarea,
  Spinner,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { COLORS } from '@shared/ui';
import { GroupSearch } from '@shared/ui';
import type { Account } from '@shared/entities/account/model/types';
import { useCreateNote } from '@shared/entities/annotation';
import type { NoteType } from '@shared/entities/annotation/model/types';
import { NoteTagButton } from './NoteTagButton';
import { useNoteConfig } from './useNoteConfig';

// =============================================================================
// Styles
// =============================================================================

const useStyles = makeStyles({
  overlay: {
    position: 'fixed',
    bottom: 0,
    right: '28px',
    zIndex: 1100,
    width: '420px',
    maxWidth: 'calc(100vw - 56px)',
    backgroundColor: '#fff',
    border: `1px solid ${COLORS.border}`,
    borderBottom: 'none',
    borderRadius: '8px 8px 0 0',
    boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: `1px solid ${COLORS.border}`,
    backgroundColor: COLORS.primary,
    color: '#fff',
    borderRadius: '8px 8px 0 0',
  },
  headerTitle: {
    fontSize: '14px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  headerHint: {
    fontWeight: '400',
    opacity: 0.8,
    fontSize: '12px',
  },
  closeBtn: {
    padding: '4px',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '4px',
    color: 'rgba(255,255,255,0.8)',
    ':hover': {
      backgroundColor: 'rgba(255,255,255,0.2)',
      color: '#fff',
    },
  },
  body: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  tags: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
  },
  textarea: {
    minHeight: '70px',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 16px',
    borderTop: `1px solid ${COLORS.border}`,
    backgroundColor: COLORS.bgBase,
  },
  footerHint: {
    fontSize: '11px',
    color: COLORS.textMuted,
  },
  groupChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: 'rgba(63,76,92,0.1)',
    border: '1px solid #A4C0C1',
    borderRadius: '4px',
    fontSize: '13px',
  },
  groupChipName: {
    fontWeight: '600',
    flex: 1,
  },
  groupChipClear: {
    background: 'none',
    border: 'none',
    color: COLORS.textSecondary,
    cursor: 'pointer',
    fontSize: '12px',
  },
});

// =============================================================================
// Component
// =============================================================================

interface QuickNotePanelProps {
  open: boolean;
  onDismiss: () => void;
  defaultAccountId?: string;
  defaultAccountName?: string;
}

export function QuickNotePanel({ open, onDismiss, defaultAccountId, defaultAccountName }: QuickNotePanelProps) {
  const styles = useStyles();
  const createNote = useCreateNote();
  const { noteTypes, noteTags } = useNoteConfig();

  const [selectedAccount, setSelectedAccount] = useState<Account | null>(
    defaultAccountId ? { id: defaultAccountId, name: defaultAccountName ?? '' } as Account : null,
  );
  const [noteType, setNoteType] = useState<string>('General Note');
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [content, setContent] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Reset when panel opens
  useEffect(() => {
    if (open) {
      if (defaultAccountId) {
        setSelectedAccount({ id: defaultAccountId, name: defaultAccountName ?? '' } as Account);
      }
      setNoteType('General Note');
      setSelectedTags(new Set());
      setContent('');
      setError('');
      setSuccess('');
    }
  }, [open, defaultAccountId, defaultAccountName]);

  const handleToggleTag = useCallback((tag: string) => {
    setSelectedTags(prev => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  }, []);

  const handlePost = useCallback(async () => {
    if (!selectedAccount || !content.trim()) return;
    setError('');
    try {
      await createNote.mutateAsync({
        accountId: selectedAccount.id,
        noteType: noteType as NoteType,
        tags: [...selectedTags] as any,
        content: content.trim(),
      });
      setSuccess('Note posted to Dynamics timeline');
      setContent('');
      setSelectedTags(new Set());
      setTimeout(() => {
        setSuccess('');
        onDismiss();
      }, 1500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to post note');
    }
  }, [selectedAccount, noteType, selectedTags, content, createNote, onDismiss]);

  if (!open) return null;

  const isPreset = Boolean(defaultAccountId);

  return (
    <div className={styles.overlay}>
      {/* Header */}
      <div className={styles.header}>
        <span className={styles.headerTitle}>
          Quick Note
          <span className={styles.headerHint}>→ Dynamics Timeline</span>
        </span>
        <button className={styles.closeBtn} onClick={onDismiss}>
          <DismissRegular />
        </button>
      </div>

      {/* Body */}
      <div className={styles.body}>
        {/* Group selection */}
        {!isPreset && !selectedAccount && (
          <GroupSearch
            onSelect={setSelectedAccount}
            selectedAccount={selectedAccount}
            placeholder="Search for a group..."
          />
        )}
        {selectedAccount && (
          <div className={styles.groupChip}>
            <span className={styles.groupChipName}>{selectedAccount.name}</span>
            {!isPreset && (
              <button className={styles.groupChipClear} onClick={() => setSelectedAccount(null)}>
                change
              </button>
            )}
          </div>
        )}

        {/* Note type */}
        <Select
          value={noteType}
          onChange={(_, d) => setNoteType(d.value)}
        >
          {noteTypes.map((t) => (
            <option key={t.value} value={t.value}>{t.value}</option>
          ))}
        </Select>

        {/* Tags */}
        <div className={styles.tags}>
          {noteTags.map((tag) => (
            <NoteTagButton
              key={tag.value}
              tag={tag}
              selected={selectedTags.has(tag.value)}
              onToggle={handleToggleTag}
            />
          ))}
        </div>

        {/* Content */}
        <Textarea
          className={styles.textarea}
          value={content}
          onChange={(_, d) => setContent(d.value)}
          placeholder="What's happening? This will post to the group's Dynamics CRM timeline..."
          resize="vertical"
        />

        {/* Status */}
        {error && (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}
        {success && (
          <MessageBar intent="success">
            <MessageBarBody>{success}</MessageBarBody>
          </MessageBar>
        )}
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <span className={styles.footerHint}>Ctrl+N to toggle · Esc to close</span>
        <Button
          appearance="primary"
          size="small"
          onClick={handlePost}
          disabled={!selectedAccount || !content.trim() || createNote.isPending}
          icon={createNote.isPending ? <Spinner size="tiny" /> : undefined}
        >
          {createNote.isPending ? 'Posting...' : 'Post Note'}
        </Button>
      </div>
    </div>
  );
}
